import { memo } from "react";
import { Handle, Position } from "reactflow";
import { motion } from "framer-motion";
import { ChevronRight, ChevronDown } from "lucide-react";
import type { AreaGroupNodeData } from "./useRoadmapLayout";
import { STATUS_COLORS, NODE_SPRING, AREA_NODE_WIDTH } from "./constants";

interface Props {
  data: AreaGroupNodeData & { revealDelay?: number };
}

function AreaGroupNodeInner({ data }: Props) {
  const { area, isExpanded, revealDelay = 0 } = data;
  const topics = area.topics ?? [];

  const statusColor =
    area.status === "known" ? STATUS_COLORS.known :
    area.status === "in_progress" ? STATUS_COLORS.next :
    STATUS_COLORS.to_learn;

  return (
    <div style={{ width: AREA_NODE_WIDTH, cursor: "pointer" }}>
      <Handle type="target" position={Position.Top} className="!bg-transparent !border-0 !w-3 !h-1.5 !min-h-0" />

      <motion.div
        initial={{ opacity: 0, scale: 0.88, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ ...NODE_SPRING, delay: revealDelay }}
      >
        <div
          className="rounded-2xl border-2 bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
          style={{ borderColor: statusColor.border }}
        >
          <div className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-semibold text-gray-900 leading-snug">
                  {area.label}
                </h4>
                <p className="text-[11px] text-gray-400 mt-1 leading-snug">
                  {area.description}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                <span className="text-[11px] font-medium text-gray-400 tabular-nums">
                  {topics.length}
                </span>
                {isExpanded
                  ? <ChevronDown className="w-4 h-4 text-gray-400" />
                  : <ChevronRight className="w-4 h-4 text-gray-400" />
                }
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <Handle type="source" id="bottom" position={Position.Bottom} className="!bg-transparent !border-0 !w-3 !h-1.5 !min-h-0" />
      <Handle type="source" id="right" position={Position.Right} className="!bg-transparent !border-0 !w-1.5 !h-3 !min-w-0" />
    </div>
  );
}

export const AreaGroupNode = memo(AreaGroupNodeInner);
